import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { MsalGuard, MsalRedirectComponent } from "@azure/msal-angular";
import { SlideAnimationTrigger } from "src/app/animations/slide-animation";
import { TechCoverComponent } from "src/app/components/tech-cover/tech-cover.component";
import { TechCoverCreateTicketFormComponent as TechCoverCreateTicketFormComponent } from "src/app/components/tech-cover/tech-cover-create-ticket-form/tech-cover-create-ticket-form.component";
import Constants from "src/app/constants/constants";

import { AdminComponent } from "./components/admin/admin/admin.component";
import { AdminRemindersComponent } from "./components/admin/admin-reminders/admin-reminders.component";
import { ApprovedServicePriceComponent } from "./components/admin/approved-service-price/approved-service-price.component";
import { CompanyRemindersComponent } from "./components/admin/company-reminders/company-reminders.component";
import { ContactsComponent } from "./components/admin/contacts/contacts.component";
import { CountryRemindersComponent } from "./components/admin/country-reminders/country-reminders.component";
import { CustomersComponent } from "./components/admin/customers/customers.component";
import { DocumentFiltersComponent } from "./components/admin/document-filters/document-filters.component";
import { LocalizationComponent } from "./components/admin/localization/localization.component";
import { LocalizationEditComponent } from "./components/admin/localization-edit/localization-edit.component";
import { PendingUsersComponent } from "./components/admin/pending-users/pending-users.component";
import { RequestedServiceListComponent } from "./components/admin/requested-service-list/requested-service-list.component";
import { UserComponent } from "./components/admin/user/user.component";
import { UsersComponent } from "./components/admin/users/users.component";
import { VisibleEventComponent } from "./components/admin/visible-event/visible-event.component";
import { ContractDetailsComponent } from "./components/contract-details/contract-details.component";
import { ContractDocumentsComponent } from "./components/contract-documents/contract-documents.component";
import { ContractInfoComponent } from "./components/contract-info/contract-info.component";
import { ContractItemsComponent } from "./components/contract-items/contract-items.component";
import { ContractToolsComponent } from "./components/contract-tools/contract-tools.component";
import { ContractsComponent } from "./components/contracts/contracts.component";
import { FieldServiceComponent } from "./components/field-service/field-service.component";
import { FieldServiceDetailsComponent } from "./components/field-service-details/field-service-details.component";
import { FieldServiceHistoryComponent } from "./components/field-service-history/field-service-history.component";
import { FieldServiceInfoComponent } from "./components/field-service-info/field-service-info.component";
import { FieldServiceToolsComponent } from "./components/field-service-tools/field-service-tools.component";
import { HomeComponent } from "./components/home/home.component";
import { LoginComponent } from "./components/login/login.component";
import { LoginFailedComponent } from "./components/login-failed/login-failed.component";
import { NoAccessComponent } from "./components/no-access/no-access.component";
import { NotFoundComponent } from "./components/not-found/not-found.component";
import { PurchaseOrderComponent } from "./components/purchase-order/purchase-order.component";
import { RemindersComponent } from "./components/reminders/reminders.component";
import { RequestAccountComponent } from "./components/request-account/request-account.component";
import { ServiceRequestComponent } from "./components/service-request/service-request.component";
import { TechCoverListComponent } from "./components/tech-cover/tech-cover-list/tech-cover-list.component";
import { ToolDetailsComponent } from "./components/tool-details/tool-details.component";
import { ToolHistoryComponent } from "./components/tool-history/tool-history.component";
import { ToolInfoComponent } from "./components/tool-info/tool-info.component";
import { ToolLocationComponent } from "./components/tool-location/tool-location.component";
import { ToolStatusComponent } from "./components/tool-status/tool-status.component";
import { ToolsComponent } from "./components/tools/tools.component";
import RouteNameConstants from "./constants/route-name-constants";
import { techCoverGuard } from "./guards/tech-cover-auth-guard";
import { AdminAuthGuard } from "./services/admin-auth-guard.service";
import { AdminOrCountryAdminAuthGuard } from "./services/admin-or-country-admin-auth-guard.service";
import { AuthGuard } from "./services/auth-guard.service";
import { SubscriptionAuthGuard } from "./services/subscription-auth-guard.service";

/**  === Important! ==
 * Each route must provide a value for its `data` property.
 * Route `path` and `data` must match - you cannot put arbitrary value in `data`.
 * It should reflect the path (without params) in case of absolute path. E.g. path: "tool/:company/:serialnumber"
 * the data prop would be "/tool" - taking the path without params (company and serialNumber).
 * It's important to have leading `/` for absolute path, since `data` prop is used in navigation inside
 * the component or templates.  For relative path  (e.g. when you define child route and it's defined
 * relative to it's parent) then `data` prop should be without leading `/`. See definition for existing child
 * route components for more detail.
 */

const routes: Routes = [
  {
    path: "",
    component: HomeComponent,
    pathMatch: "full",
    canActivate: [AuthGuard, MsalGuard],
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.ROOT,
    },
  },
  {
    title: "Home",
    path: "home",
    redirectTo: "",
    pathMatch: "full",
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.HOME,
    },
  },
  {
    title: "Tools",
    path: "tools",
    component: ToolsComponent,
    pathMatch: "full",
    canActivate: [AuthGuard, MsalGuard],
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.TOOLS,
    },
  },
  // TODO: Remove this route - should be only one view My Tools
  {
    title: "Tools",
    path: "tools/:view",
    component: ToolsComponent,
    pathMatch: "full",
    canActivate: [AuthGuard, MsalGuard],
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.TOOLS,
    },
  },
  {
    title: "Tool details",
    path: "tool/:company/:serialnumber",
    component: ToolDetailsComponent,
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.TOOLS_DETAILS_BASE,
    },
    canActivate: [AuthGuard, MsalGuard],
    children: [
      {
        path: "",
        redirectTo: "status",
        pathMatch: "full",
        data: {
          [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.TOOLS_DETAILS_CHILD_STATUS,
        },
      },
      {
        title: "Tool details - status",
        path: "status",
        component: ToolStatusComponent,
        data: {
          [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.TOOLS_DETAILS_CHILD_STATUS,
        },
      },
      {
        title: "Tool details - history",
        path: "history",
        component: ToolHistoryComponent,
        data: {
          [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.TOOLS_DETAILS_CHILD_HISTORY,
        },
      },
      {
        title: "Tool details - info",
        path: "info",
        component: ToolInfoComponent,
        data: {
          [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.TOOLS_DETAILS_CHILD_INFO,
        },
      },
    ],
  },
  {
    title: "Location",
    path: "location",
    component: ToolLocationComponent,
    pathMatch: "full",
    canActivate: [AuthGuard, MsalGuard],
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.TOOL_LOCATION,
    },
  },
  {
    title: "Contracts",
    path: "contracts",
    component: ContractsComponent,
    pathMatch: "full",
    canActivate: [AuthGuard, MsalGuard, SubscriptionAuthGuard],
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.CONTRACTS,
    },
  },
  {
    title: "Contract details",
    path: "contracts/:companyId/:contractReference",
    component: ContractDetailsComponent,
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.CONTRACTS_DETAILS_BASE,
    },
    canActivate: [AuthGuard, MsalGuard],
    children: [
      {
        path: "",
        redirectTo: "info",
        pathMatch: "full",
        data: {
          [RouteNameConstants.ROUTE_NAME_DATA_PROP]:
            RouteNameConstants.CONTRACTS_DETAILS_CHILD_INFO,
        },
      },
      {
        title: "Contract details - info",
        path: "info",
        component: ContractInfoComponent,
        data: {
          [RouteNameConstants.ROUTE_NAME_DATA_PROP]:
            RouteNameConstants.CONTRACTS_DETAILS_CHILD_INFO,
        },
      },
      {
        title: "Contract details - tools",
        path: "tools",
        component: ContractToolsComponent,
        data: {
          [RouteNameConstants.ROUTE_NAME_DATA_PROP]:
            RouteNameConstants.CONTRACTS_DETAILS_CHILD_TOOLS,
        },
      },
      {
        title: "Contract details - items",
        path: "items",
        component: ContractItemsComponent,
        data: {
          [RouteNameConstants.ROUTE_NAME_DATA_PROP]:
            RouteNameConstants.CONTRACTS_DETAILS_CHILD_ITEMS,
        },
      },
      {
        title: "Contract details - documents",
        path: "documents",
        component: ContractDocumentsComponent,
        data: {
          [RouteNameConstants.ROUTE_NAME_DATA_PROP]:
            RouteNameConstants.CONTRACTS_DETAILS_CHILD_DOCUMENTS,
        },
      },
    ],
  },
  {
    title: "Login",
    path: "login",
    component: LoginComponent,
    pathMatch: "full",
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.LOGIN,
    },
  },
  {
    title: "Reminders",
    path: "reminders",
    component: RemindersComponent,
    pathMatch: "full",
    canActivate: [AuthGuard, MsalGuard, SubscriptionAuthGuard],
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.REMINDERS,
    },
  },
  {
    title: "Field service",
    path: "field-service",
    component: FieldServiceComponent,
    pathMatch: "full",
    canActivate: [AuthGuard, MsalGuard],
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.FIELD_SERVICE,
    },
  },
  {
    title: "Field service details",
    path: "field-service/:status/:companyId/:jobNumber",
    component: FieldServiceDetailsComponent,
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.FIELD_SERVICE_BASE,
    },
    canActivate: [AuthGuard, MsalGuard],
    children: [
      {
        path: "",
        redirectTo: "info",
        pathMatch: "full",
        data: {
          [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.FIELD_SERVICE_CHILD_INFO,
        },
      },
      {
        title: "Field service details - info",
        path: "info",
        component: FieldServiceInfoComponent,
        data: {
          [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.FIELD_SERVICE_CHILD_INFO,
        },
      },
      {
        title: "Field service details - tools",
        path: "tools",
        component: FieldServiceToolsComponent,
        data: {
          [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.FIELD_SERVICE_CHILD_TOOLS,
        },
      },
      {
        title: "Field service details - history",
        path: "history",
        component: FieldServiceHistoryComponent,
        data: {
          [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.FIELD_SERVICE_CHILD_HISTORY,
        },
      },
    ],
  },
  {
    title: "TechCover",
    path: "tech-cover",
    component: TechCoverComponent,
    canActivate: [AuthGuard, MsalGuard, SubscriptionAuthGuard, techCoverGuard],
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.TECH_COVER,
    },
    children: [
      {
        path: "",
        redirectTo: "list",
        pathMatch: "full",
        data: {
          [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.TECH_COVER_CHILD_LIST,
        },
      },
      {
        path: "list",
        component: TechCoverListComponent,
        data: {
          [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.TECH_COVER_CHILD_LIST,
        },
      },
      {
        path: "create-ticket",
        component: TechCoverCreateTicketFormComponent,
        data: {
          [Constants.ROUTE_DATA_ANIMATION_PROP]: SlideAnimationTrigger,
          [RouteNameConstants.ROUTE_NAME_DATA_PROP]:
            RouteNameConstants.TECH_COVER_CHILD_CREATE_TICKET,
        },
      },
    ],
  },
  {
    title: "Admin",
    path: "admin",
    component: AdminComponent,
    pathMatch: "full",
    canActivate: [AuthGuard, MsalGuard, AdminAuthGuard],
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.ADMIN,
    },
  },
  {
    title: "Admin - New pending user",
    path: "admin/users/new/:pendingId",
    component: UserComponent,
    pathMatch: "full",
    canActivate: [AuthGuard, MsalGuard, AdminAuthGuard],
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.ADMIN_USERS_NEW,
    },
  },
  {
    title: "Admin - New user",
    path: "admin/users/new",
    component: UserComponent,
    pathMatch: "full",
    canActivate: [AuthGuard, MsalGuard, AdminAuthGuard],
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.ADMIN_USERS_NEW,
    },
  },
  {
    title: "Admin - Pending users",
    path: "admin/users/pending",
    component: PendingUsersComponent,
    pathMatch: "full",
    canActivate: [AuthGuard, MsalGuard, AdminAuthGuard],
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.ADMIN_USERS_PENDING,
    },
  },
  {
    title: "Admin - User",
    path: "admin/users/:id",
    component: UserComponent,
    pathMatch: "full",
    canActivate: [AuthGuard, MsalGuard, AdminAuthGuard],
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.ADMIN_USERS,
    },
  },
  {
    title: "Admin - Users",
    path: "admin/users",
    component: UsersComponent,
    pathMatch: "full",
    canActivate: [AuthGuard, MsalGuard, AdminAuthGuard],
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.ADMIN_USERS,
    },
  },
  {
    title: "Admin - customers",
    path: "admin/customers",
    component: CustomersComponent,
    pathMatch: "full",
    canActivate: [AuthGuard, MsalGuard, AdminAuthGuard],
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.ADMIN_CUSTOMERS,
    },
  },
  {
    title: "Admin - Localization for the language",
    path: "admin/localization/:id",
    component: LocalizationEditComponent,
    pathMatch: "full",
    canActivate: [AuthGuard, MsalGuard, AdminAuthGuard],
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.ADMIN_LOCALIZATION,
    },
  },
  {
    title: "Admin - Localizations",
    path: "admin/localization",
    component: LocalizationComponent,
    pathMatch: "full",
    canActivate: [AuthGuard, MsalGuard, AdminAuthGuard],
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.ADMIN_LOCALIZATION,
    },
  },
  {
    title: "Admin - Visible events",
    path: "admin/visible-event",
    component: VisibleEventComponent,
    pathMatch: "full",
    canActivate: [AuthGuard, MsalGuard, AdminAuthGuard],
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.ADMIN_VISIBLE_EVENT,
    },
  },
  {
    title: "Admin - Document filter",
    path: "admin/document-filter",
    component: DocumentFiltersComponent,
    pathMatch: "full",
    canActivate: [AuthGuard, MsalGuard, AdminAuthGuard],
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.ADMIN_DOCUMENT_FILTER,
    },
  },
  {
    title: "Admin - Requested service list",
    path: "admin/requested-service-list",
    component: RequestedServiceListComponent,
    pathMatch: "full",
    canActivate: [AuthGuard, MsalGuard, AdminAuthGuard],
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.ADMIN_REQUESTED_SERVICE_LIST,
    },
  },
  {
    title: "Admin - Approved service price",
    path: "admin/approved-service-price",
    component: ApprovedServicePriceComponent,
    pathMatch: "full",
    canActivate: [AuthGuard, MsalGuard, AdminAuthGuard],
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.ADMIN_APPROVED_SERVICE_PRICE,
    },
  },
  {
    title: "Admin - Contacts",
    path: "admin/contacts",
    component: ContactsComponent,
    pathMatch: "full",
    canActivate: [AuthGuard, MsalGuard, AdminAuthGuard],
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.ADMIN_CONTACTS,
    },
  },
  {
    path: "admin/reminder-list",
    component: AdminRemindersComponent,
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.ADMIN_REMINDER_LIST_BASE,
    },
    canActivate: [AuthGuard, MsalGuard, AdminOrCountryAdminAuthGuard],
    children: [
      {
        path: "",
        redirectTo: "country",
        pathMatch: "full",
        data: {
          [RouteNameConstants.ROUTE_NAME_DATA_PROP]:
            RouteNameConstants.ADMIN_REMINDER_LIST_CHILD_COUNTRY,
        },
      },
      {
        title: "Admin - Country reminders",
        path: "country",
        component: CountryRemindersComponent,
        data: {
          [RouteNameConstants.ROUTE_NAME_DATA_PROP]:
            RouteNameConstants.ADMIN_REMINDER_LIST_CHILD_COUNTRY,
        },
      },
      {
        title: "Admin - Company reminders",
        path: "company",
        component: CompanyRemindersComponent,
        data: {
          [RouteNameConstants.ROUTE_NAME_DATA_PROP]:
            RouteNameConstants.ADMIN_REMINDER_LIST_CHILD_COMPANY,
        },
      },
    ],
  },
  {
    title: "No access",
    path: "no-access",
    component: NoAccessComponent,
    pathMatch: "full",
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.NO_ACCESS,
    },
  },
  {
    title: "Request account",
    path: "request-account",
    component: RequestAccountComponent,
    pathMatch: "full",
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.REQUEST_ACCOUNT,
    },
  },
  {
    path: "service-request",
    title: "Service request",
    component: ServiceRequestComponent,
    pathMatch: "full",
    canActivate: [AuthGuard, MsalGuard, SubscriptionAuthGuard],
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.SERVICE_REQUEST,
    },
  },
  {
    path: "purchase-order",
    title: "Purchase order",
    component: PurchaseOrderComponent,
    pathMatch: "full",
    canActivate: [AuthGuard, MsalGuard, SubscriptionAuthGuard],
    data: {
      [RouteNameConstants.ROUTE_NAME_DATA_PROP]: RouteNameConstants.PURCHASE_ORDER,
    },
  },
  {
    path: "auth",
    component: MsalRedirectComponent,
    title: "Authorization",
  },
  {
    path: "login-failed",
    component: LoginFailedComponent,
  },
  {
    path: "**",
    component: NotFoundComponent,
    pathMatch: "full",
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
